<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import CardLayout from 'dashboard/components-next/CardLayout.vue';

const props = defineProps({
  order: { type: Object, required: true },
});

const router = useRouter();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const label = computed(
  () => props.order.invoiceNumber || props.order.number || `#${props.order.id}`
);

const money = computed(() => {
  const amount = Number(props.order.receivableTotal ?? props.order.total ?? 0);
  const currency = props.order.currency || 'BDT';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(amount);
});

const navigateTo = () => {
  router.push(
    accountScopedRoute('web_order_order_detail', {
      orderId: props.order.id,
    })
  );
};
</script>

<template>
  <button type="button" class="w-full text-left" @click="navigateTo">
    <CardLayout
      layout="row"
      class="[&>div]:items-start [&>div]:justify-start [&>div]:px-4 [&>div]:py-3 hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
    >
      <span
        class="i-lucide-shopping-bag mt-0.5 block size-4 shrink-0 text-n-slate-11"
        aria-hidden="true"
      />
      <div class="flex min-w-0 flex-1 flex-col gap-1.5">
        <div class="flex min-w-0 items-center justify-between gap-2">
          <h5 class="min-w-0 truncate py-1 text-sm font-medium text-n-slate-12">
            {{ label }}
          </h5>
          <span class="shrink-0 text-sm text-n-slate-11">{{ money }}</span>
        </div>
        <div
          class="grid min-w-0 grid-cols-[minmax(0,max-content)_auto_minmax(0,max-content)_auto_minmax(0,max-content)] items-center gap-3 overflow-hidden text-sm"
        >
          <span
            v-if="order.customerName"
            class="min-w-0 truncate text-n-slate-11"
            :title="order.customerName"
          >
            {{ order.customerName }}
          </span>
          <div
            v-if="order.customerName && order.customerPhone"
            class="h-3 w-px rounded bg-n-slate-6"
          />
          <span
            v-if="order.customerPhone"
            class="min-w-0 truncate text-n-slate-11"
            :title="order.customerPhone"
          >
            {{ order.customerPhone }}
          </span>
          <div
            v-if="(order.customerName || order.customerPhone) && order.status"
            class="h-3 w-px rounded bg-n-slate-6"
          />
          <span
            v-if="order.status"
            class="min-w-0 truncate capitalize text-n-slate-11"
          >
            {{ order.status.replace(/-/g, ' ') }}
          </span>
        </div>
        <p v-if="order.storeName" class="truncate text-xs text-n-slate-10">
          {{
            t('WEB_ORDER.PRODUCTS.STORE_NAME_VALUE', { name: order.storeName })
          }}
        </p>
      </div>
    </CardLayout>
  </button>
</template>
